import React from 'react';
import { cn } from '../../utils/cn';
import type { BadgeProps } from '../../types/components';

const badgeVariants = {
  primary: 'bg-primary-100 text-primary-800 border-primary-200',
  secondary: 'bg-secondary-100 text-secondary-800 border-secondary-200',
  outline: 'bg-white text-gray-700 border-gray-300',
  ghost: 'bg-gray-100 text-gray-700 border-transparent',
  success: 'bg-success-100 text-success-800 border-success-200',
  warning: 'bg-warning-100 text-warning-800 border-warning-200',
  error: 'bg-error-100 text-error-800 border-error-200',
  info: 'bg-info-100 text-info-800 border-info-200',
};

const badgeSizes = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
  xl: 'px-4 py-2 text-lg',
};

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      dot = false,
      outline = false,
      children,
      className,
      testId,
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'inline-flex items-center',
      'font-medium rounded-full',
      'border',
      'transition-colors duration-200',
    ];

    const variantClasses = badgeVariants[variant] || badgeVariants.primary;
    const sizeClasses = badgeSizes[size] || badgeSizes.md;

    const classes = cn(
      baseClasses,
      variantClasses,
      sizeClasses,
      {
        'border-2': outline,
        'bg-transparent': outline,
      },
      className
    );

    if (dot) {
      return (
        <span
          ref={ref}
          className={cn(
            'inline-block w-2 h-2 rounded-full',
            variantClasses.split(' ')[0], // Extract background color
            className
          )}
          data-testid={testId}
          {...props}
        />
      );
    }

    return (
      <span
        ref={ref}
        className={classes}
        data-testid={testId}
        {...props}
      >
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';